import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { AIChatCitationFragment } from 'types/graphql'
import { v4 as uuidv4 } from 'uuid'

import useDisclosure, { UseDisclosureReturn } from 'src/hooks/use-disclosure'
import { usePersistentIds } from 'src/hooks/use-persistent-ids'
import { useSlug } from 'src/hooks/use-slug'

type ChatStorageIds = {
  chatLocalStorageId: string
  chatSessionStorageId: string
}

export type CitizenAIChatMessage = {
  id: string | number
  text: string
  sender: 'user' | 'ai'
  questionCreatedAt?: string
  responseCreatedAt?: string
  isThinking?: boolean
  citations?: AIChatCitationFragment[]
}

export type CitizenAIViewMode = 'side' | 'full'

type OrgContext = {
  orgSlug: string | null
  orgDisplayName: string | null
}

export type CitizenAIAssistantContextValue = {
  overlay: UseDisclosureReturn
  deferredClose: () => void
  viewMode: CitizenAIViewMode
  setViewMode: (mode: CitizenAIViewMode) => void
  openSideView: () => void
  openFullView: () => void
  maximize: () => void
  minimize: () => void
  messages: CitizenAIChatMessage[]
  setMessages: React.Dispatch<React.SetStateAction<CitizenAIChatMessage[]>>
  isAssistantTyping: boolean
  setIsAssistantTyping: React.Dispatch<React.SetStateAction<boolean>>
  userTextInput: string
  setUserTextInput: React.Dispatch<React.SetStateAction<string>>
  searchQuery: string
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
  activeSessionId: number | null
  chatStorageIds: ChatStorageIds
  isLoadingSession: boolean
  setActiveSessionId: (sessionId: number | null) => void
  setIsLoadingSession: (loading: boolean) => void
  startNewConversation: () => void
  currentOrgContext: OrgContext
  setCurrentOrgContext: (context: OrgContext) => void
  isGenericMode: boolean
}

const CitizenAIAssistantContext = createContext<CitizenAIAssistantContextValue | null>(null)

export const useCitizenAIAssistant = (): CitizenAIAssistantContextValue | null => {
  return useContext(CitizenAIAssistantContext)
}

type Props = {
  children: React.ReactNode
}

const useNewChatStorageIds = (browserId: string): (() => ChatStorageIds) => {
  return useCallback(() => {
    const uniqueId = uuidv4()
    return {
      chatLocalStorageId: `${browserId}-citizen-chat-${uniqueId}`,
      chatSessionStorageId: `${uniqueId}-citizen-chat`,
    }
  }, [browserId])
}

export const CitizenAIAssistantProvider = ({ children }: Props) => {
  const overlay = useDisclosure()
  const [viewMode, setViewMode] = useState<CitizenAIViewMode>('side')
  const [messages, setMessages] = useState<CitizenAIChatMessage[]>([])
  const [isAssistantTyping, setIsAssistantTyping] = useState(false)
  const [userTextInput, setUserTextInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null)
  const [isLoadingSession, setIsLoadingSession] = useState(false)
  const { browserId } = usePersistentIds()
  const generateNewChatStorageIds = useNewChatStorageIds(browserId)

  const [chatStorageIds, setChatStorageIds] = useState<ChatStorageIds>(() =>
    generateNewChatStorageIds()
  )

  const routeSlug = useSlug()
  const userOverrodeOrgRef = useRef(false)

  const [currentOrgContext, setCurrentOrgContext] = useState<OrgContext>({
    orgSlug: routeSlug || null,
    orgDisplayName: null,
  })

  const handleSetCurrentOrgContext = useCallback((ctx: OrgContext) => {
    userOverrodeOrgRef.current = true
    setCurrentOrgContext(ctx)
  }, [])

  const prevRouteSlugRef = useRef(routeSlug)
  useEffect(() => {
    if (routeSlug !== prevRouteSlugRef.current) {
      prevRouteSlugRef.current = routeSlug
      userOverrodeOrgRef.current = false
    }
    // Intentional: the `routeSlug` truthiness check prevents clearing orgSlug when navigating to a page without a slug
    if (routeSlug && routeSlug !== currentOrgContext.orgSlug && !userOverrodeOrgRef.current) {
      setCurrentOrgContext((prev) => ({
        ...prev,
        orgSlug: routeSlug,
      }))
    }
  }, [routeSlug, currentOrgContext.orgSlug])

  const isGenericMode = !currentOrgContext.orgSlug

  const startNewConversation = useCallback(() => {
    setMessages([])
    setIsAssistantTyping(false)
    setUserTextInput('')
    setActiveSessionId(null)
    setChatStorageIds(generateNewChatStorageIds())
    setIsLoadingSession(false)
  }, [generateNewChatStorageIds])

  const handleSetActiveSessionId = useCallback((sessionId: number | null) => {
    setActiveSessionId(sessionId)
    if (sessionId === null) {
      setIsLoadingSession(false)
    }
  }, [])

  const pendingCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const deferredClose = useCallback(() => {
    if (pendingCloseRef.current !== null) {
      clearTimeout(pendingCloseRef.current)
    }
    pendingCloseRef.current = setTimeout(() => {
      pendingCloseRef.current = null
      overlay.close()
    }, 0)
  }, [overlay])

  useEffect(() => {
    return () => {
      if (pendingCloseRef.current !== null) {
        clearTimeout(pendingCloseRef.current)
      }
    }
  }, [])

  const openSideView = useCallback(() => {
    setViewMode('side')
    overlay.open()
  }, [overlay])

  const openFullView = useCallback(() => {
    if (pendingCloseRef.current !== null) {
      clearTimeout(pendingCloseRef.current)
      pendingCloseRef.current = null
    }
    setViewMode('full')
    overlay.open()
  }, [overlay])

  const maximize = useCallback(() => {
    setViewMode('full')
  }, [])

  const minimize = useCallback(() => {
    setViewMode('side')
  }, [])

  const value = useMemo<CitizenAIAssistantContextValue>(
    () => ({
      overlay,
      deferredClose,
      viewMode,
      setViewMode,
      openSideView,
      openFullView,
      maximize,
      minimize,
      messages,
      setMessages,
      isAssistantTyping,
      setIsAssistantTyping,
      userTextInput,
      setUserTextInput,
      searchQuery,
      setSearchQuery,
      activeSessionId,
      chatStorageIds,
      isLoadingSession,
      setActiveSessionId: handleSetActiveSessionId,
      setIsLoadingSession,
      startNewConversation,
      currentOrgContext,
      setCurrentOrgContext: handleSetCurrentOrgContext,
      isGenericMode,
    }),
    [
      overlay,
      deferredClose,
      viewMode,
      openSideView,
      openFullView,
      maximize,
      minimize,
      messages,
      isAssistantTyping,
      userTextInput,
      searchQuery,
      activeSessionId,
      chatStorageIds,
      isLoadingSession,
      handleSetActiveSessionId,
      startNewConversation,
      currentOrgContext,
      handleSetCurrentOrgContext,
      isGenericMode,
    ]
  )

  return (
    <CitizenAIAssistantContext.Provider value={value}>
      {children}
    </CitizenAIAssistantContext.Provider>
  )
}
